import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import FrenchStartupBadge from "./components/FrenchStartupBadge";

// App version for cache busting
const APP_VERSION = "2.2.0";

// Check and clear cache on version mismatch
const checkVersion = () => {
  const storedVersion = localStorage.getItem('tireo_app_version');
  if (storedVersion !== APP_VERSION) {
    console.log(`Version mismatch: ${storedVersion} -> ${APP_VERSION}. Clearing cache...`);
    localStorage.setItem('tireo_app_version', APP_VERSION);
    // Clear service worker caches
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }
    // Unregister service workers
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister();
        });
      });
    }
  }
};

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import Dashboard from "./pages/Dashboard";
import CreateDraw from "./pages/CreateDraw";
import EditDraw from "./pages/EditDraw";
import CreatePoll from "./pages/CreatePoll";
import EditPoll from "./pages/EditPoll";
import PublicDraw from "./pages/PublicDraw";
import PublicPoll from "./pages/PublicPoll";
import PaymentSuccess from "./pages/PaymentSuccess";
import ShopMode from "./pages/ShopMode";
import MembersList from "./pages/MembersList";
import GdprDelete from "./pages/GdprDelete";
import CreateGame from "./pages/CreateGame";
import EditGame from "./pages/EditGame";
import PublicGame from "./pages/PublicGame";
import ServicesPage from "./pages/ServicesPage";
import PublicDrawResults from "./pages/PublicDrawResults";
import PublicPollResults from "./pages/PublicPollResults";
import PublicDrawShow from "./pages/PublicDrawShow";
import PublicDrawProjector from "./pages/PublicDrawProjector";
import PublicDrawShowResults from "./pages/PublicDrawShowResults";
import AdminDashboard from "./pages/AdminDashboard";
import AdminLoginPage from "./pages/AdminLoginPage";
import DrawEditor from "./pages/DrawEditor";
import MemberAuth from "./pages/MemberAuth";
import MemberProfile from "./pages/MemberProfile";
import PlayPortal from "./pages/PlayPortal";
import ParticipantLists from "./pages/ParticipantLists";
import EmailSettings from "./pages/EmailSettings";
import GamifiedDrawCreator from "./pages/GamifiedDrawCreator";
import BillingPage from "./pages/BillingPage";
import AdminBillingPage from "./pages/AdminBillingPage";
import SettingsPage from "./pages/SettingsPage";
import CompanySettingsPage from "./pages/CompanySettingsPage";
import AccountSettingsPage from "./pages/AccountSettingsPage";
import DrawShopMode from "./pages/DrawShopMode";
import LegalServiceDetail from "./pages/LegalServiceDetail";
import JuristDashboard from "./pages/JuristDashboard";
import DrawCertificate from "./pages/DrawCertificate";
import OrganizerEmailSettings from "./pages/OrganizerEmailSettings";
import Contact from "./pages/Contact";
import MediaGallery from "./pages/MediaGallery";
import PublicPhotoWall from "./pages/PublicPhotoWall";
import PublicPhotoWallView from "./pages/PublicPhotoWallView";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  // Check version on app load
  useEffect(() => {
    checkVersion();
  }, []);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/draw/:id/live" element={<PublicDraw />} />
      <Route path="/draw/:id/register" element={<PublicDraw />} />
      <Route path="/draw/:id/results" element={<PublicDrawResults />} />
      <Route path="/draw/:id/show" element={<PublicDrawShow />} />
      <Route path="/draw/:id/show/projector" element={<PublicDrawProjector />} />
      <Route path="/draw/:id/show/results" element={<PublicDrawShowResults />} />
      <Route path="/poll/:id/vote" element={<PublicPoll />} />
      <Route path="/poll/:id/results" element={<PublicPollResults />} />
      <Route path="/draw/:id/photos" element={<PublicPhotoWall />} />
      <Route path="/draw/:id/wall" element={<PublicPhotoWallView />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/billing" element={<ProtectedRoute><AdminBillingPage /></ProtectedRoute>} />
      <Route path="/draws/new" element={<ProtectedRoute><GamifiedDrawCreator /></ProtectedRoute>} />
      <Route path="/draws/new/simple" element={<ProtectedRoute><CreateDraw /></ProtectedRoute>} />
      <Route path="/draws/:id" element={<ProtectedRoute><DrawEditor /></ProtectedRoute>} />
      <Route path="/draws/:id/edit" element={<ProtectedRoute><DrawEditor /></ProtectedRoute>} />
      <Route path="/draw/:id/edit" element={<ProtectedRoute><DrawEditor /></ProtectedRoute>} />
      <Route path="/draw/:id/certificate" element={<ProtectedRoute><DrawCertificate /></ProtectedRoute>} />
      <Route path="/polls/new" element={<ProtectedRoute><CreatePoll /></ProtectedRoute>} />
      <Route path="/polls/:id" element={<ProtectedRoute><EditPoll /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/shop-mode" element={<ProtectedRoute><ShopMode /></ProtectedRoute>} />
      <Route path="/members" element={<ProtectedRoute><MembersList /></ProtectedRoute>} />
      <Route path="/lists" element={<ProtectedRoute><ParticipantLists /></ProtectedRoute>} />
      <Route path="/settings/email" element={<ProtectedRoute><OrganizerEmailSettings /></ProtectedRoute>} />
      <Route path="/settings/company" element={<ProtectedRoute><CompanySettingsPage /></ProtectedRoute>} />
      <Route path="/settings/account" element={<ProtectedRoute><AccountSettingsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/games/new" element={<ProtectedRoute><CreateGame /></ProtectedRoute>} />
      <Route path="/games/:id" element={<ProtectedRoute><EditGame /></ProtectedRoute>} />
      <Route path="/services" element={<ProtectedRoute><ServicesPage /></ProtectedRoute>} />
      <Route path="/legal-services/:id" element={<ProtectedRoute><LegalServiceDetail /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/media" element={<ProtectedRoute><MediaGallery /></ProtectedRoute>} />
      
      {/* Public Game */}
      <Route path="/game/:id" element={<PublicGame />} />
      
      {/* Contact Page */}
      <Route path="/contact" element={<Contact />} />
      
      {/* GDPR Public */}
      <Route path="/gdpr/delete/:token" element={<GdprDelete />} />
      
      {/* Draw Shop Mode (public) */}
      <Route path="/draw/:id/shop" element={<DrawShopMode />} />
      
      {/* Jurist Portal */}
      <Route path="/jurist" element={<JuristDashboard />} />
      
      {/* Member/Participant Portal */}
      <Route path="/join" element={<MemberAuth />} />
      <Route path="/play" element={<PlayPortal />} />
      <Route path="/play/profile" element={<MemberProfile />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
          <FrenchStartupBadge />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
